import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import { translations, stepsTranslations } from '../translations/content';
import Auth from './Auth';
import supabase from '../supabase/supabase';

const { 
  FiArrowLeft, FiArrowRight, FiCheckCircle, FiTarget, 
  FiLock, FiRocket, FiZap, FiShield, FiMail, FiUserPlus,
  FiSave, FiLoader, FiLogIn
} = FiIcons;

const StepDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const t = translations[lang].detail;
  const p = translations[lang].paths;
  const steps = stepsTranslations[lang];
  const stepId = parseInt(id);
  const step = steps.find(s => s.id === stepId);

  const [hasUnlockedAccess, setHasUnlockedAccess] = useState(false);
  const [user, setUser] = useState(null);

  // Step 1 Vision State
  const [step1Data, setStep1Data] = useState({
    vision_statement: '',
    problem_id: '',
    root_cause: '',
    solution_hypothesis: ''
  });
  const [isSavingStep1, setIsSavingStep1] = useState(false);
  const [isSavedStep1, setIsSavedStep1] = useState(false);

  // Step 3 Idea State
  const [step3Data, setStep3Data] = useState({
    problem_solved: '',
    target_group: '',
    suitability: '',
    product_name_slogan: ''
  });
  const [isSavingStep3, setIsSavingStep3] = useState(false);
  const [isSavedStep3, setIsSavedStep3] = useState(false);

  // Auth check & Premium Profile Sync
  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      
      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles_1768156521239')
          .select('is_premium')
          .eq('id', session.user.id)
          .single();
        
        setHasUnlockedAccess(profile?.is_premium || false);
      } else {
        setHasUnlockedAccess(false);
      }
    };

    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles_1768156521239')
          .select('is_premium')
          .eq('id', session.user.id)
          .single();
        setHasUnlockedAccess(profile?.is_premium || false);
      } else {
        setHasUnlockedAccess(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Load Data based on step
  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      if (stepId === 1) {
        const { data } = await supabase
          .from('step_1_responses_1772900080158')
          .select('*')
          .eq('user_id', user.id)
          .single();
        if (data) setStep1Data(data);
      } else if (stepId === 3) {
        const { data } = await supabase
          .from('step_3_ideas_1772902600000')
          .select('*')
          .eq('user_id', user.id)
          .single();
        if (data) setStep3Data(data);
      }
    };

    fetchData();
  }, [stepId, user]);

  const handleStep1Save = async () => {
    if (!user) return;
    setIsSavingStep1(true);
    const { error } = await supabase
      .from('step_1_responses_1772900080158')
      .upsert({ user_id: user.id, ...step1Data, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
    setIsSavingStep1(false);
    if (!error) {
      setIsSavedStep1(true);
      setTimeout(() => setIsSavedStep1(false), 3000);
    }
  };

  const handleStep3Save = async () => {
    if (!user) return;
    setIsSavingStep3(true);
    const { error } = await supabase
      .from('step_3_ideas_1772902600000')
      .upsert({ user_id: user.id, ...step3Data, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
    setIsSavingStep3(false);
    if (!error) {
      setIsSavedStep3(true);
      setTimeout(() => setIsSavedStep3(false), 3000);
    }
  };

  const renderLockedOverlay = () => (
    <div className="absolute inset-x-0 top-0 bottom-0 z-20 flex items-center justify-center p-6 bg-blue-900/10 backdrop-blur-[12px] rounded-[2.5rem]">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white/95 p-8 md:p-10 rounded-[2.5rem] shadow-2xl border border-blue-100 max-w-lg w-full"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiLock} className="text-3xl text-blue-600" />
          </div>
          <h4 className="text-2xl font-black text-gray-900 mb-3 uppercase tracking-tight">
            {lang === 'de' ? 'Inhalt gesperrt' : 'Content Locked'}
          </h4>
          <p className="text-gray-600 font-medium">
            {lang === 'de' 
              ? 'Melde dich an, um sofortigen Zugriff auf alle Tools zu erhalten.' 
              : 'Sign in to get instant access to all tools.'}
          </p>
        </div>
        
        <div className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100">
          <Auth embedded={true} initialIsLogin={false} />
        </div>
      </motion.div>
    </div>
  );

  if (!step) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 pt-24 text-center"><Link to="/steps" className="text-blue-600">Step not found</Link></div>;
  }

  const nextStepId = stepId === 10 ? null : stepId + 1;
  const placeholderTasks = [
    lang === 'de' ? 'Detaillierter Aktionsplan für Phase 1' : 'Detailed action plan for Phase 1',
    lang === 'de' ? 'Spezifische Checkliste zur Validierung' : 'Specific validation checklist',
    lang === 'de' ? 'Vorlagen und Ressourcen für diesen Schritt' : 'Templates and resources for this step',
    lang === 'de' ? 'Messbare Meilensteine und KPIs' : 'Measurable milestones and KPIs'
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-0 pt-24">
      <div className="max-w-6xl mx-auto px-4 mb-20">
        <div className="flex items-center justify-between mb-8">
          <Link to={lang === 'de' ? '/schritte' : '/steps'} className="group flex items-center space-x-2 text-gray-500 hover:text-blue-600 transition-colors bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100 italic">
            <SafeIcon icon={FiArrowLeft} className="transition-transform group-hover:-translate-x-1" />
            <span className="text-sm font-bold">{t.back}</span>
          </Link>
          <span className="text-sm font-bold text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{t.stepOf.replace('{current}', stepId)}</span>
        </div>

        <div className="bg-white rounded-[2.5rem] p-8 md:p-12 mb-8 shadow-sm border border-gray-100 overflow-hidden relative">
          <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none font-black text-[200px] leading-none text-blue-900">{stepId}</div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10">
            <div className="inline-flex items-center space-x-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-6"><SafeIcon icon={FiTarget} /><span>{t.overview}</span></div>
            <h1 className="text-4xl md:text-6xl font-black text-gray-900 mb-4 tracking-tight leading-tight">{step.title}</h1>
            {step.quote && <div className="mb-6 pl-4 border-l-4 border-blue-500"><p className="text-xl md:text-2xl font-medium text-gray-600 italic leading-relaxed">{step.quote}</p><p className="text-sm font-black text-blue-600 uppercase tracking-widest mt-2">— {step.quoteAuthor}</p></div>}
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-[2.5rem] p-8 md:p-10 shadow-sm border border-gray-100 relative">
              <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center"><SafeIcon icon={FiTarget} className="text-blue-600" /></div>
                <span>{t.overview}</span>
              </h2>
              <div className="space-y-6">
                <p className="text-lg text-gray-700 leading-relaxed font-medium">{step.description}</p>
                <p className="text-gray-600 leading-relaxed">{step.content}</p>
              </div>
            </div>

            {/* Premium Content Wrapper (Tools & Tasks) */}
            <div className="relative">
              <div className={`space-y-8 transition-all duration-700 ${!hasUnlockedAccess ? 'blur-xl grayscale opacity-30 select-none' : ''}`}>
                {/* Step 1 Vision Tool */}
                {stepId === 1 && (
                  <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem]">
                    <div className="flex items-center space-x-3 mb-8">
                      <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center"><SafeIcon icon={FiRocket} className="text-blue-600 text-xl" /></div>
                      <h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step1ToolTitle}</h3>
                    </div>
                    <div className="space-y-8">
                      {['problem_solved', 'target_group', 'suitability', 'product_name_slogan'].map((key, i) => (
                        <div key={key} className="space-y-3">
                          <label className="block text-sm font-black text-gray-900 uppercase tracking-widest">{t[`step1Question${i+1}`]}</label>
                          <textarea value={step1Data[key]} onChange={(e) => setStep1Data({ ...step1Data, [key]: e.target.value })} placeholder={t.step1Placeholder} className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl p-4 focus:border-blue-500 focus:ring-0 transition-all min-h-[100px] font-medium text-gray-700" />
                        </div>
                      ))}
                      <div className="pt-4 flex items-center justify-between">
                        <button onClick={handleStep1Save} disabled={isSavingStep1 || !user} className={`flex items-center space-x-2 px-8 py-4 rounded-xl font-black transition-all ${isSavedStep1 ? 'bg-green-100 text-green-700' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>{isSavingStep1 ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={isSavedStep1 ? FiCheckCircle : FiSave} />}<span>{isSavingStep1 ? t.step1Saving : (isSavedStep1 ? t.step1Saved : t.step1Save)}</span></button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3 Idea Tool */}
                {stepId === 3 && (
                  <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem]">
                    <div className="flex items-center space-x-3 mb-8"><div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center"><SafeIcon icon={FiRocket} className="text-blue-600 text-xl" /></div><h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step3ToolTitle}</h3></div>
                    <div className="space-y-8">
                      {['problem_solved', 'target_group', 'suitability', 'product_name_slogan'].map((key, i) => (
                        <div key={key} className="space-y-3">
                          <label className="block text-sm font-black text-gray-900 uppercase tracking-widest">{t[`step3Question${i+1}`]}</label>
                          <textarea value={step3Data[key]} onChange={(e) => setStep3Data({ ...step3Data, [key]: e.target.value })} placeholder={t.step3Placeholder} className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl p-4 focus:border-blue-500 focus:ring-0 transition-all min-h-[100px] font-medium text-gray-700" />
                        </div>
                      ))}
                      <div className="pt-4 flex items-center justify-between">
                        <button onClick={handleStep3Save} disabled={isSavingStep3 || !user} className={`flex items-center space-x-2 px-8 py-4 rounded-xl font-black transition-all ${isSavedStep3 ? 'bg-green-100 text-green-700' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>{isSavingStep3 ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={isSavedStep3 ? FiCheckCircle : FiSave} />}<span>{isSavingStep3 ? t.step3Saving : (isSavedStep3 ? t.step3Saved : t.step3Save)}</span></button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Items */}
                <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-8 md:p-10 text-white shadow-xl rounded-[2.5rem]">
                  <div className="flex items-center space-x-3 mb-8"><div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm"><SafeIcon icon={FiCheckCircle} className="text-white text-xl" /></div><h3 className="text-2xl font-black uppercase tracking-widest">{t.actionItems}</h3></div>
                  <div className="space-y-4">
                    {(hasUnlockedAccess ? step.actionItems : placeholderTasks).map((item, i) => (
                      <div key={i} className="flex items-start space-x-4 p-5 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/20 transition-all">
                        <div className="mt-1 min-w-[24px] w-6 h-6 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center">{hasUnlockedAccess ? <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div> : <SafeIcon icon={FiLock} className="text-[10px]" />}</div>
                        <span className="font-medium text-white leading-relaxed text-base">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Single Locked Overlay */}
              {!hasUnlockedAccess && renderLockedOverlay()}
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-gray-900 rounded-[2.5rem] p-8 text-white shadow-xl sticky top-24 border border-gray-800">
              <div className="flex items-center space-x-3 mb-6"><SafeIcon icon={FiTarget} className="text-blue-400 text-xl" /><h3 className="font-black uppercase tracking-widest text-sm">{t.keyFocus}</h3></div>
              <ul className="space-y-4 mb-8">
                {step.keyPoints.map((point, i) => {
                  const isLocked = !hasUnlockedAccess && i >= 2;
                  return (
                    <li key={i} className={`flex items-center space-x-3 p-4 rounded-2xl bg-white/5 border border-white/5 transition-all duration-500 ${isLocked ? 'opacity-30 blur-[1px]' : 'text-gray-300'}`}>
                      <SafeIcon icon={isLocked ? FiLock : FiArrowRight} className={`${isLocked ? 'text-gray-500' : 'text-blue-400'} text-xs flex-shrink-0`} />
                      <span className={`font-bold text-sm ${isLocked ? 'text-gray-500' : ''}`}>{isLocked ? 'Locked' : point}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="h-px bg-white/10 my-8" />
              <div className="text-center">
                <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4 text-blue-400"><SafeIcon icon={hasUnlockedAccess ? FiCheckCircle : FiShield} className="text-2xl" /></div>
                <h3 className="font-black text-white mb-2 text-lg">{hasUnlockedAccess ? 'Access Active' : 'Access Restricted'}</h3>
                <button 
                  onClick={() => hasUnlockedAccess ? (nextStepId && navigate(lang === 'de' ? `/schritte/${nextStepId}` : `/steps/${nextStepId}`)) : navigate(p.register)} 
                  className="w-full mt-4 bg-white text-gray-900 hover:bg-blue-50 font-black py-4 rounded-xl flex items-center justify-center space-x-2 transition-colors"
                >
                  <span>{hasUnlockedAccess ? (nextStepId ? `${t.continueTo} ${nextStepId}` : 'All complete!') : 'Register for Access'}</span>
                  <SafeIcon icon={FiArrowRight} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepDetail;